import {createColumnHelper, getExpandedRowModel} from '@tanstack/react-table';
import {EditButton, ShowButton, DocumentButton} from 'components/refinedev/action_buttons';
import {defaultQueryOptions} from 'pages/_utils';
import StateBoundary from '@/components/basic/StateBoundary';
import formatter from '@/lib/formatter';
import {DataTableColumnHeader} from '@/components/data-table/data-table-column-header';
import {DataTable} from '@/components/data-table/data-table';
import DefaultDataTableToolbar from '@/components/refinedev/data-table/DefaultDataTableToolbar';
import useRefineTanstackDataTable from '@/hooks/refinedev/useRefineTanstackDataTable';
import {BaseKey, CanAccess, useCan} from '@refinedev/core';
import {CreateTaskDialogButton} from './components/task-create-dialog-button';
import {UserIconList} from '@/pages/_shared/user-icon-list';
import MySelect from '@/components/basic/Select';
import {TaskStatuses} from './types';
import {TaskEditDialogButton} from './components/task-edit-dialog-button';
import TaskFilters from './components/task-filter';
import {useEffect} from 'react';
import {useSearchParams} from 'react-router-dom';

const thisResource = 'tasks';

const columnHelper = createColumnHelper<any>();

const columns = [
  columnHelper.accessor('id', {
    meta: {label: 'ID'},
    header: ({column}) => <DataTableColumnHeader column={column} />,
    cell: info => info.getValue(),
  }),
  columnHelper.accessor('name', {
    meta: {label: 'Tên công việc'},
    header: ({column}) => <DataTableColumnHeader column={column} />,
    cell: info => info.renderValue(),
  }),
  columnHelper.accessor('description', {
    meta: {label: 'Mô tả'},
    header: ({column}) => <DataTableColumnHeader column={column} />,
    cell: info => info.renderValue(),
  }),
  columnHelper.accessor('project.name', {
    id: 'project.name',
    meta: {label: 'Dự án'},
    header: ({column}) => <DataTableColumnHeader column={column} />,
    cell: info => info.renderValue(),
  }),
  // các cột kế hoạch
  columnHelper.accessor('start_date', {
    meta: {label: 'Bắt đầu (KH)'},
    header: ({column}) => <DataTableColumnHeader column={column} />,
    cell: info => formatter.formatDate(info.getValue()),
  }),
  columnHelper.accessor('expected_work_days', {
    meta: {label: 'Số ngày (KH)'},
    header: ({column}) => <DataTableColumnHeader column={column} />,
    cell: info => info.renderValue(),
  }),
  columnHelper.accessor('initial_end_date', {
    meta: {label: 'Kết thúc (KH)'},
    header: ({column}) => <DataTableColumnHeader column={column} />,
    cell: info => formatter.formatDate(info.getValue()),
  }),
  // các cột kế hoạch điều chỉnh
  columnHelper.accessor('rescheduled_start_date', {
    meta: {label: 'Bắt đầu (KHĐC)'},
    header: ({column}) => <DataTableColumnHeader column={column} />,
    cell: info => formatter.formatDate(info.getValue()),
  }),
  columnHelper.accessor('rescheduled_work_days', {
    meta: {label: 'Số ngày (KHĐC)'},
    header: ({column}) => <DataTableColumnHeader column={column} />,
    cell: info => info.renderValue(),
  }),
  columnHelper.accessor('rescheduled_end_date', {
    meta: {label: 'Kết thúc (KHĐC)'},
    header: ({column}) => <DataTableColumnHeader column={column} />,
    cell: info => formatter.formatDate(info.getValue()),
  }),
  // các cột thực tế
  columnHelper.accessor('actual_start_date', {
    meta: {label: 'Bắt đầu (TT)'},
    header: ({column}) => <DataTableColumnHeader column={column} />,
    cell: info => formatter.formatDate(info.getValue()),
  }),
  columnHelper.accessor('actual_work_days', {
    meta: {label: 'Số ngày (KH)'},
    header: ({column}) => <DataTableColumnHeader column={column} />,
    cell: info => info.renderValue(),
  }),
  columnHelper.accessor('actual_end_date', {
    meta: {label: 'Kết thúc (TT)'},
    header: ({column}) => <DataTableColumnHeader column={column} />,
    cell: info => formatter.formatDate(info.getValue()),
  }),
  // ngân sách
  columnHelper.accessor('requested_budget', {
    meta: {label: 'Ngân sách đề xuất'},
    header: ({column}) => <DataTableColumnHeader column={column} />,
    cell: info => info.renderValue(),
  }),
  columnHelper.accessor('approved_budget', {
    meta: {label: 'Ngân sách'},
    header: ({column}) => <DataTableColumnHeader column={column} />,
    cell: info => info.renderValue(),
  }),
  // thành viên
  columnHelper.accessor('members', {
    meta: {label: 'Thành viên'},
    header: ({column}) => <DataTableColumnHeader column={column} />,
    cell: info => <UserIconList users={info.getValue()} maxDisplay={4} />,
    enableSorting: false,
  }),
  columnHelper.accessor('status', {
    meta: {label: 'Trạng thái'},
    header: ({column}) => <DataTableColumnHeader column={column} />,
    cell: info => TaskStatuses.find(v => v.id === info.getValue())?.label || '',
    enableSorting: false,
  }),
  columnHelper.display({
    id: 'actions',
    meta: {label: 'Hành động'},
    header: ({column}) => <DataTableColumnHeader column={column} />,
    cell: ({row}) => (
      <div className="flex flex-row">
        <ShowButton
          id={String(row.original.id)}
          resource={thisResource}
          canParams={{
            my_role: row.original.my_role,
            my_project_role: row.original.my_project_role,
          }}
        />
        <TaskEditDialogButton
          id={String(row.original.id)}
          canParams={{
            my_role: row.original.my_role,
            my_project_role: row.original.my_project_role,
          }}
        />
        {/* <EditButton id={String(row.original.id)} resource={thisResource} /> */}
        {/* <EditButtonWorkflow id={String(info.row.getValue('id'))} resource={thisResource} /> */}

        {/* <DeleteButtonWithConfirmation
                  id={String(row.original.id)}
                  resource={thisResource}
                  // forceCan={row.original.my_role === 'editor'}
                /> */}
        <DocumentButton
          resource="projects"
          id={String(row.original.project.id)}
          canParams={{
            my_role: row.original.my_project_role,
          }}
        />
      </div>
    ),
  }),
];

const TaskList = () => {
  const [searchParams, setSearchParams] = useSearchParams();

  // const _columns = project_id ? columns.filter(column => column.id !== 'project_id') : columns;
  const {refineTable, tanstackTable} = useRefineTanstackDataTable(
    {
      resource: thisResource,
      sorters: {initial: [{field: 'id', order: 'asc'}]},
      // filters: project_id
      //   ? {permanent: [{field: 'project_id', operator: 'eq', value: project_id}]}
      //   : undefined,
      filters: {
        initial: searchParams.get('status')
          ? [
              {
                field: 'status',
                operator: 'eq',
                value: searchParams.get('status'),
              },
            ]
          : [],
      },
      queryOptions: {
        ...defaultQueryOptions,
      },
      syncWithLocation: true,
      meta: {
        with: ['members', 'project'],
        // ...(project_id ? [] : ['project'])],
      },
    },
    {
      columns,
      initialState: {
        columnVisibility: {
          expected_work_days: false,
          initial_end_date: false,
          rescheduled_start_date: false,
          rescheduled_work_days: false,
          rescheduled_end_date: false,
          actual_start_date: false,
          actual_work_days: false,
          requested_budget: false,
        },
      },
    },
  );

  useEffect(() => {
    const status = searchParams.get('status');
    if (status) {
      searchParams.delete('status');
      setSearchParams(searchParams);
    }
  }, [searchParams, setSearchParams]);

  return (
    <StateBoundary
      isLoading={refineTable.tableQuery.isLoading}
      error={refineTable.tableQuery.error}>
      <div className="p-2">
        <DataTable
          table={tanstackTable}
          toolbar={
            <DefaultDataTableToolbar
              resource={thisResource}
              table={tanstackTable}
              createButton={false}
              // createButton={
              //   project_id ? (
              //     <CreateTaskDialogButton
              //       project_id={project_id}
              //       onSuccess={() => refineTable.tableQuery.refetch()}
              //     />
              //   ) : (
              //     false
              //   )
              // }
              // filters={!project_id && <TaskFilters table={refineTable} />}
              // viewOptionsStorageKey={project_id ? 'task-list-project' : 'task-list'}
              filters={<TaskFilters table={refineTable} />}
              viewOptionsStorageKey="task-list"
            />
          }
        />
      </div>
    </StateBoundary>
  );
};

const TaskListWithPermission = () => {
  return (
    <CanAccess
      resource={thisResource}
      action="list"
      fallback={<>Bạn không có quyền truy cập trang này </>}>
      <TaskList />
    </CanAccess>
  );
};

TaskList.table_columns = columns;

export default TaskList;
